import type {
	BulletListDefinition,
	ListItemDefinition,
	MentionDefinition,
	ParagraphDefinition,
} from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';

import type { PeopleEntityElementType, PeopleEntityListElementType } from '../../layout/types';
import { getUniquePeopleEntitiesFromList } from '../../layout/utils';
import type { TagConverter } from '../types';

const buildMentionText = (name?: string): string => {
	if (!name) {
		return '@';
	}
	return name.startsWith('@') ? name : `@${name}`;
};

const buildMention = (accountId: string, name?: string): MentionDefinition => ({
	type: 'mention',
	attrs: {
		id: accountId,
		text: buildMentionText(name),
		userType: 'DEFAULT',
		accessLevel: 'CONTAINER',
	},
});

/**
 * Converts `<people-entity>` to an ADF paragraph wrapping a mention node.
 *
 * Used for both `UserInlinePreview` (single mention pill in a paragraph) and
 * `UserProfilePreview` (we don't have a profile URL on the Lumina node, so we
 * fall back to a mention — the editor will render the rich profile-card preview
 * popup on hover anyway).
 *
 * Returns undefined if the node has no accountId.
 */
export const convertPeopleEntity: TagConverter = (luminaNode): ParagraphDefinition | undefined => {
	const attributes = (luminaNode as PeopleEntityElementType).attributes;
	const accountId = attributes?.accountId;
	const name = attributes?.name?.trim();
	if (!accountId) {
		return undefined;
	}

	if (fg('kd_lumina_convert_people_as_adf')) {
		return {
			type: 'paragraph',
			content: [buildMention(accountId, name)],
		};
	}

	// Pre-gate behaviour: text fallback so any other Lumina-to-ADF consumer
	// (besides the gated chat copy button) keeps producing plain-text output.
	return {
		type: 'paragraph',
		content: [
			{ type: 'text', text: 'Person', marks: [{ type: 'strong' }] },
			{ type: 'text', text: ` (${accountId})` },
		],
	};
};

/**
 * Converts `<people-entity-list>` to an ADF bullet list of mention nodes.
 *
 * Used for `UserInlinePreviewList` (a.k.a. UserPreviewInlineList in the spec) —
 * each child entity becomes one bullet containing a mention pill. The editor's
 * mention plugin resolves the display name on render.
 */
export const convertPeopleEntityList: TagConverter = (
	luminaNode,
): BulletListDefinition | undefined => {
	const list = luminaNode as PeopleEntityListElementType;
	const people = getUniquePeopleEntitiesFromList(list);

	const items: ListItemDefinition[] = [];

	for (const person of people) {
		const inner = convertPeopleEntity({
			tag: 'people-entity',
			attributes: person,
			children: [],
		} as PeopleEntityElementType);
		if (inner === undefined) {
			continue;
		}
		items.push({
			type: 'listItem',
			content: [inner] as ListItemDefinition['content'],
		});
	}

	if (!items.length) {
		return undefined;
	}

	return { type: 'bulletList', content: items };
};
